import type { Workspace } from '@stratumn/studio-shared';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';

type Props = {
  workspace: Workspace | null;
  workflowNames?: Record<string, string>;
  onClose: () => void;
  onConfirm: () => Promise<void> | void;
};

export const DeleteWorkspaceDialog = ({
  workspace,
  workflowNames = {},
  onClose,
  onConfirm
}: Props) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [draftConfirmed, setDraftConfirmed] = useState(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset confirmation when workspace changes
  useEffect(() => {
    setDraftConfirmed(false);
  }, [workspace?.id]);

  const publishedWorkflowEntries = Object.entries(
    workspace?.published_workflow_ids ?? {}
  );
  const hasPublishedWorkflows = publishedWorkflowEntries.length > 0;

  const handleConfirm = async () => {
    setIsDeleting(true);
    try {
      await onConfirm();
    } finally {
      setIsDeleting(false);
    }
  };

  const handleClose = () => {
    setDraftConfirmed(false);
    onClose();
  };

  return (
    <Dialog
      onOpenChange={open => {
        if (!(open || isDeleting)) {
          handleClose();
        }
      }}
      open={!!workspace}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Delete workspace</DialogTitle>
          <DialogDescription className="text-pretty">
            Are you sure you want to delete workspace{' '}
            <span className="font-medium text-foreground">
              {workspace?.name}
            </span>
            ? This action cannot be undone.
          </DialogDescription>
        </DialogHeader>

        {hasPublishedWorkflows && (
          <div className="space-y-3 px-6 py-4">
            <p className="text-muted-foreground text-sm">
              The following{' '}
              {publishedWorkflowEntries.length === 1 ? 'workflow' : 'workflows'}{' '}
              will be deactivated on Trace:
            </p>
            <ul className="list-disc pl-5 text-sm">
              {publishedWorkflowEntries.map(([studioId, traceId]) => (
                <li key={studioId}>
                  {workflowNames[studioId] ?? studioId}{' '}
                  <span className="font-mono text-muted-foreground text-xs">
                    ({traceId})
                  </span>
                </li>
              ))}
            </ul>
            <label className="flex cursor-pointer items-center gap-2.5">
              <Checkbox
                checked={draftConfirmed}
                className="border-destructive data-[state=checked]:border-destructive data-[state=checked]:bg-destructive"
                onCheckedChange={checked => setDraftConfirmed(checked === true)}
              />
              <span className="select-none text-destructive text-sm">
                I confirm the above{' '}
                {publishedWorkflowEntries.length === 1
                  ? 'workflow'
                  : 'workflows'}{' '}
                will be deactivated
              </span>
            </label>
          </div>
        )}

        <DialogFooter>
          <Button disabled={isDeleting} onClick={handleClose} variant="outline">
            Cancel
          </Button>
          <Button
            disabled={isDeleting || (hasPublishedWorkflows && !draftConfirmed)}
            onClick={handleConfirm}
            variant="destructive"
          >
            {isDeleting ? 'Deleting...' : 'Delete this workspace'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
